import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Smartphone, ShieldCheck, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CODE_TTL = 10 * 60;          // 10 minutes
const RESEND_COOLDOWN = 60;        // 60 seconds

function formatSeconds(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * Reusable SMS-OTP modal backed by Twilio Verify on the server.
 *
 * Props:
 *  - open              boolean
 *  - phone             string   (the phone to verify; E.164 or 10-digit)
 *  - purpose           string   ('registration' | 'login' | 'checkout')
 *  - allowSkip         boolean  (show a "Skip for now" button)
 *  - authToken         string?  (JWT; required for server-side skip flagging)
 *  - title             string?
 *  - description       string?
 *  - onVerified        (resp) => void
 *  - onSkip            () => void
 *  - onClose           () => void
 *  - autoSend          boolean  (send code on mount; default true)
 */
export default function PhoneVerificationModal({
  open,
  phone,
  purpose = 'registration',
  allowSkip = true,
  authToken = null,
  title = 'Verify Your Phone Number',
  description = "We'll text you a 6-digit code to confirm your number.",
  onVerified,
  onSkip,
  onClose,
  autoSend = true,
}) {
  const [step, setStep] = useState('send');       // 'send' | 'verify'
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [resendIn, setResendIn] = useState(0);    // seconds until resend allowed
  const [expiresIn, setExpiresIn] = useState(0);  // seconds until code expires
  const [error, setError] = useState('');
  const sentOnceRef = useRef(false);

  // Reset state when opened / phone changes
  useEffect(() => {
    if (!open) return;
    setCode('');
    setError('');
    setStep('send');
    setResendIn(0);
    setExpiresIn(0);
    sentOnceRef.current = false;
  }, [open, phone]);

  // Auto-send the initial code
  useEffect(() => {
    if (open && autoSend && phone && !sentOnceRef.current) {
      sentOnceRef.current = true;
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoSend, phone]);

  // Countdown timers
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      setResendIn((s) => (s > 0 ? s - 1 : 0));
      setExpiresIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [open]);

  const handleSend = async () => {
    if (!phone) {
      setError('A phone number is required.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/phone/send-code/`, { phone, purpose });
      setStep('verify');
      setResendIn(RESEND_COOLDOWN);
      setExpiresIn(CODE_TTL);
      toast.success(`Code sent to ***${String(phone).slice(-4)}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not send the verification code. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/phone/verify-code/`, {
        phone,
        code,
        purpose,
      });
      toast.success('Phone number verified!');
      onVerified?.(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Verification failed. Please try again.';
      setError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.post(`${API_URL}/api/auth/phone/skip/`, { phone }, { headers });
      toast.warning('Verification skipped. You can verify later from your account.');
      onSkip?.();
    } catch (err) {
      // Even if flagging fails, still let the user proceed — this is an optional flow.
      onSkip?.();
    } finally {
      setSkipping(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="phone-verification-modal"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
          aria-label="Close"
          data-testid="phone-verify-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-rsc-navy/10">
            {step === 'verify' ? (
              <ShieldCheck className="w-7 h-7 text-rsc-navy" />
            ) : (
              <Smartphone className="w-7 h-7 text-rsc-navy" />
            )}
          </div>

          <h2 className="text-2xl font-heading font-bold text-center text-rsc-navy mb-1" data-testid="phone-verify-title">
            {title}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">{description}</p>

          {step === 'send' && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-center">
                <p className="text-xs text-gray-500">Sending code to</p>
                <p className="font-mono text-lg text-gray-900">{phone || '—'}</p>
              </div>
              {error && (
                <p className="text-sm text-red-600 text-center" data-testid="phone-verify-error">{error}</p>
              )}
              <Button
                onClick={handleSend}
                disabled={sending || !phone}
                className="w-full h-11 bg-rsc-navy hover:bg-rsc-navy-dark text-white font-semibold"
                data-testid="phone-verify-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
                {sending ? 'Sending...' : 'Send Verification Code'}
              </Button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-4">
              <p className="text-center text-sm text-gray-600">
                Enter the 6-digit code we sent to <strong>***{String(phone).slice(-4)}</strong>
              </p>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, ''));
                  setError('');
                }}
                placeholder="000000"
                className="text-center text-3xl tracking-[0.5em] font-mono h-14"
                autoFocus
                data-testid="phone-verify-code-input"
              />
              {expiresIn > 0 && (
                <p className="text-xs text-gray-400 text-center">
                  Code expires in {formatSeconds(expiresIn)}
                </p>
              )}
              {error && (
                <p className="text-sm text-red-600 text-center" data-testid="phone-verify-error">{error}</p>
              )}
              <Button
                onClick={handleVerify}
                disabled={verifying || code.length !== 6}
                className="w-full h-11 bg-rsc-navy hover:bg-rsc-navy-dark text-white font-semibold"
                data-testid="phone-verify-submit-btn"
              >
                {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {verifying ? 'Verifying...' : 'Verify Code'}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setStep('send');
                    setCode('');
                    setError('');
                  }}
                  className="text-gray-500 hover:underline"
                  data-testid="phone-verify-change-number-btn"
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={resendIn > 0 || sending}
                  className="text-rsc-navy font-semibold hover:underline disabled:opacity-40 disabled:no-underline"
                  data-testid="phone-verify-resend-btn"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          {allowSkip && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  You can skip this step, but we'll ask you to verify again later.
                  Verified numbers get <strong>priority support</strong> and class reminders.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSkip}
                disabled={skipping}
                className="w-full text-sm text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
                data-testid="phone-verify-skip-btn"
              >
                {skipping ? 'Skipping...' : 'Skip for now'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
